import React from 'react';
import {
  Box,
  Header,
  Icon,
  SpaceBetween,
  Container,
} from '@cloudscape-design/components';
import { ActivityHistory } from 'src/types/helios/Task';
import { formatDate } from 'src/utils/generalUtils';

enum TaskStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  OPEN = 'OPEN',
}

enum ChangeType {
  CREATION = 'creation',
  STATUS_CHANGE = 'status_change',
  ASSIGNEE_CHANGE = 'assignee_change',
  DUE_DATE_CHANGE = 'due_date_change',
  CANCELLATION = 'cancellation',
  UPDATE = 'update',
}

enum FieldType {
  TASK_STATUS = 'taskStatus',
  ASSIGNEE_LOGIN = 'assigneeLogin',
  DUE_DATE = 'dueDate',
  CANCELLED = 'cancelled',
  UNKNOWN = 'unknown',
}

enum ChangeIcon {
  FILE = 'file',
  REFRESH = 'refresh',
  USER_PROFILE = 'user-profile',
  CLOSE = 'close',
  EDIT = 'edit',
}

enum IconColor {
  CREATION = 'var(--color-text-status-info)',
  STATUS_CHANGE = 'var(--color-text-status-success)',
  ASSIGNEE_CHANGE = 'var(--color-text-accent)',
  CANCELLATION = 'var(--color-text-status-error)',
  DEFAULT = 'var(--color-text-body-secondary)',
}

interface ChangeLogProps {
  activityHistory?: ActivityHistory[];
}

export const ChangeLog: React.FC<ChangeLogProps> = ({ activityHistory = [] }) => {
  const parseFieldType = (field: string): FieldType => {
    if (field.startsWith('taskStatus:')) return FieldType.TASK_STATUS;
    if (field.startsWith('assigneeLogin:')) return FieldType.ASSIGNEE_LOGIN;
    if (field.startsWith('dueDate:')) return FieldType.DUE_DATE;
    if (field === 'cancelled') return FieldType.CANCELLED;
    return FieldType.UNKNOWN;
  };

  const parseFieldChange = (
    field: string,
  ): { oldValue: string; newValue: string } | null => {
    const parts = field.split(':');
    if (parts.length !== 2) return null;

    const changeParts = parts[1].split('->');
    if (changeParts.length !== 2) return null;

    return {
      oldValue: changeParts[0] || '',
      newValue: changeParts[1] || '',
    };
  };

  const getChangeIcon = (activity: ActivityHistory) => {
    if (!activity.changedFields?.length) return ChangeIcon.FILE;

    const fieldTypes = activity.changedFields.map(parseFieldType);

    if (fieldTypes.includes(FieldType.TASK_STATUS)) return ChangeIcon.REFRESH;
    if (fieldTypes.includes(FieldType.ASSIGNEE_LOGIN)) return ChangeIcon.USER_PROFILE;
    if (fieldTypes.includes(FieldType.CANCELLED)) return ChangeIcon.CLOSE;
    return ChangeIcon.EDIT;
  };

  const getChangeType = (activity: ActivityHistory) => {
    if (!activity.changedFields?.length) return ChangeType.CREATION;

    // Priority 1: Meaningful assignee changes (not to null)
    const meaningfulAssigneeChange = activity.changedFields.find((field) => {
      if (field.startsWith('assigneeLogin:')) {
        const [, change] = field.split(':');
        const [, newAssignee] = change.split('->');
        return newAssignee && newAssignee !== 'null' && newAssignee.trim() !== '';
      }
      return false;
    });
    if (meaningfulAssigneeChange) {
      return ChangeType.ASSIGNEE_CHANGE;
    }

    // Priority 2: Meaningful status changes (not to null)
    const meaningfulStatusChange = activity.changedFields.find((field) => {
      if (field.startsWith('taskStatus:')) {
        const [, change] = field.split(':');
        const [, newStatus] = change.split('->');
        return newStatus && newStatus !== 'null';
      }
      return false;
    });
    if (meaningfulStatusChange) {
      return ChangeType.STATUS_CHANGE;
    }

    // Priority 3: Meaningful due date changes (not to null)
    const meaningfulDueDateChange = activity.changedFields.find((field) => {
      if (field.startsWith('dueDate:')) {
        const change = field.substring('dueDate:'.length);
        const [, newDueDate] = change.split('->');
        return newDueDate && newDueDate !== 'null';
      }
      return false;
    });
    if (meaningfulDueDateChange) {
      return ChangeType.DUE_DATE_CHANGE;
    }

    if (activity.changedFields.includes('cancelled')) return ChangeType.CANCELLATION;
    return ChangeType.UPDATE;
  };

  const getChangeMessage = (activity: ActivityHistory) => {
    const type = getChangeType(activity);

    switch (type) {
      case ChangeType.CREATION:
        return 'Task created and assigned';
      case ChangeType.STATUS_CHANGE: {
        // Extract status from changedFields if available
        const statusField = activity.changedFields?.find(
          (field) => parseFieldType(field) === FieldType.TASK_STATUS,
        );
        if (statusField) {
          const change = parseFieldChange(statusField);
          if (!change) return 'Task updated';
          const newStatus = change.newValue;
          // Skip status changes to null as they're not meaningful
          if (newStatus === 'null') return null;

          switch (newStatus) {
            case TaskStatus.IN_PROGRESS: {
              // Check if this is a reopen (transitioning FROM completed)
              if (change.oldValue === TaskStatus.COMPLETED) {
                return 'Task reopened';
              }
              return 'Task updated to In Progress';
            }
            case TaskStatus.COMPLETED:
              return 'Task updated to Completed';
            case TaskStatus.CANCELLED:
              return 'Task Cancelled';
            case TaskStatus.OPEN:
              return 'Task updated to Open';
            default:
              return 'Task updated';
          }
        }
        return 'Task updated';
      }
      case ChangeType.ASSIGNEE_CHANGE: {
        const assigneeField = activity.changedFields?.find(
          (field) => parseFieldType(field) === FieldType.ASSIGNEE_LOGIN,
        );
        if (assigneeField) {
          const change = parseFieldChange(assigneeField);
          if (!change) return 'Task reassigned';

          if (
            change.newValue &&
            change.newValue !== 'null' &&
            change.newValue.trim() !== ''
          ) {
            return `Task assigned to ${change.newValue}`;
          }
          // Skip assignee changes to null as they're not meaningful
          if (change.newValue === 'null') return null;
        }
        return 'Task reassigned';
      }
      case ChangeType.DUE_DATE_CHANGE: {
        const dueDateField = activity.changedFields?.find((field) =>
          field.startsWith('dueDate:'),
        );
        if (dueDateField) {
          const change = dueDateField.substring('dueDate:'.length);
          const [, newDueDate] = change.split('->');
          // Skip due date changes to null as they're not meaningful
          if (newDueDate === 'null') return null;
          if (newDueDate) {
            const formattedDate = formatDate(newDueDate, null, 'DD MMM YYYY');
            return `Due date updated to ${formattedDate}`;
          }
          return 'Due date updated';
        }
        return 'Due date updated';
      }
      case ChangeType.CANCELLATION:
        return 'Task Cancelled';
      default:
        return null; // Don't show generic 'Task updated' messages
    }
  };

  const getChangeDetails = (activity: ActivityHistory) => {
    const type = getChangeType(activity);

    switch (type) {
      case ChangeType.CREATION:
        return ''; // No additional details for creation
      case ChangeType.STATUS_CHANGE: {
        // Check if this is a cancellation status change
        const statusField = activity.changedFields?.find(
          (field) => parseFieldType(field) === FieldType.TASK_STATUS,
        );
        if (statusField) {
          const change = parseFieldChange(statusField);
          if (change?.newValue === TaskStatus.CANCELLED) {
            return activity.reason ? `Cancellation Reason: ${activity.reason}` : '';
          }
          // Reopen case: show reason and original completion info
          if (
            change?.oldValue === TaskStatus.COMPLETED &&
            change?.newValue === TaskStatus.IN_PROGRESS
          ) {
            const details: string[] = [];
            if (activity.reason) {
              details.push(`Reason: ${activity.reason}`);
            }
            const completedAtField = activity.changedFields?.find((f) =>
              f.startsWith('completedAt:'),
            );
            const completedLateField = activity.changedFields?.find((f) =>
              f.startsWith('completedLate:'),
            );
            if (completedAtField) {
              const completedAtChange = parseFieldChange(completedAtField);
              if (completedAtChange?.oldValue && completedAtChange.oldValue !== 'null') {
                const completedDate = formatDate(
                  completedAtChange.oldValue,
                  null,
                  'DD MMM YYYY',
                );
                const completedLateChange = completedLateField
                  ? parseFieldChange(completedLateField)
                  : null;
                const wasLate = completedLateChange?.oldValue === 'true';
                details.push(
                  `Originally completed${wasLate ? ' late' : ''} on ${completedDate}`,
                );
              }
            }
            return details.join('\n');
          }
        }
        // Show comments only if they exist and are different from the default reason
        return activity.reason && activity.reason !== 'Status updated'
          ? activity.reason
          : '';
      }
      case ChangeType.ASSIGNEE_CHANGE:
        return activity.reassignmentReason || '';
      case ChangeType.DUE_DATE_CHANGE:
        return activity.reason || '';
      case ChangeType.CANCELLATION:
        return activity.reason ? `Cancellation Reason: ${activity.reason}` : '';
      default:
        return activity.reason || '';
    }
  };

  const getIconColor = (activity: ActivityHistory) => {
    const type = getChangeType(activity);
    switch (type) {
      case ChangeType.CREATION:
        return IconColor.CREATION;
      case ChangeType.STATUS_CHANGE:
        return IconColor.STATUS_CHANGE;
      case ChangeType.ASSIGNEE_CHANGE:
        return IconColor.ASSIGNEE_CHANGE;
      case ChangeType.CANCELLATION:
        return IconColor.CANCELLATION;
      default:
        return IconColor.DEFAULT;
    }
  };

  // Sort by timestamp (latest first, newest at top)
  const sortedHistory = [...activityHistory].sort((a, b) => {
    const timeA = new Date(a.changedAt || a.createdAt || '').getTime();
    const timeB = new Date(b.changedAt || b.createdAt || '').getTime();
    return timeB - timeA; // Latest first
  });

  const timelineItems = sortedHistory
    .map((activity, index) => {
      const type = getChangeType(activity);
      const message = getChangeMessage(activity);
      if (message === null) return null;

      return (
        <Box
          key={`activity-${activity.changedAt || activity.createdAt}-${index}`}
          padding="s"
          data-testid="timeline-item"
        >
          <SpaceBetween direction="horizontal" size="s" alignItems="start">
            <Icon name={getChangeIcon(activity)} size="normal" />
            <div style={{ flex: 1 }}>
              <Box fontWeight="bold" margin={{ bottom: 'xs' }}>
                {message}
              </Box>
              {getChangeDetails(activity) && (
                <Box
                  fontSize="body-s"
                  color="text-body-secondary"
                  margin={{ bottom: 'xs' }}
                >
                  <span style={{ whiteSpace: 'pre-line' }}>
                    {getChangeDetails(activity)}
                  </span>
                </Box>
              )}
              <Box fontSize="body-s" color="text-body-secondary">
                By {activity.changedBy || activity.createdBy} •{' '}
                {(() => {
                  const timestamp = activity.changedAt || activity.createdAt;
                  if (!timestamp) return 'Unknown date';

                  const date = new Date(
                    isNaN(Number(timestamp)) ? timestamp : Number(timestamp),
                  );
                  return isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleString();
                })()}
              </Box>
            </div>
          </SpaceBetween>
        </Box>
      );
    })
    .filter(Boolean);

  return (
    <Container
      header={
        <Header variant="h3">
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <Icon key="calendar-icon" name="calendar" />
            <span key="header-text">Change Log</span>
          </SpaceBetween>
        </Header>
      }
    >
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {timelineItems.length > 0 ? (
          <SpaceBetween size="xs">{timelineItems}</SpaceBetween>
        ) : (
          <Box textAlign="center" color="text-body-secondary">
            No activity history available
          </Box>
        )}
      </div>
    </Container>
  );
};
